import React from 'react';
import type { OpportunitySignal, ScoreBreakdownProps } from '../types/opportunitySignal';

const sortSignals = (signals: OpportunitySignal[]) =>
  [...signals].sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

const contributionClass = (contribution: number): string => {
  if (contribution > 0) return 'text-emerald-600 dark:text-emerald-400';
  if (contribution < 0) return 'text-red-600 dark:text-red-400';
  return 'text-fg-muted';
};

const barClass = (contribution: number): string => {
  if (contribution > 0) return 'bg-emerald-500/70';
  if (contribution < 0) return 'bg-red-500/70';
  return 'bg-fg-muted/40';
};

interface SignalRowProps {
  signal: OpportunitySignal;
  maxContribution: number;
}

function SignalRow({ signal, maxContribution }: SignalRowProps) {
  const widthPct =
    maxContribution > 0 ? Math.min(100, (Math.abs(signal.contribution) / maxContribution) * 100) : 0;
  const sign = signal.contribution > 0 ? '+' : signal.contribution < 0 ? '−' : '';

  return (
    <li className="space-y-1">
      <div className="flex items-start justify-between text-sm">
        <div className="min-w-0 flex-1">
          <span className="font-medium text-fg">{signal.label}</span>
          {signal.evidence ? (
            <p className="text-xs text-fg-muted mt-0.5 leading-snug">{signal.evidence}</p>
          ) : null}
        </div>
        <span className={`shrink-0 tabular-nums text-xs font-semibold ${contributionClass(signal.contribution)}`}>
          {sign}
          {Math.abs(signal.contribution).toFixed(1)}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-surface-muted overflow-hidden" aria-hidden="true">
        <div className={`h-full rounded-full ${barClass(signal.contribution)}`} style={{ width: `${widthPct}%` }} />
      </div>
    </li>
  );
}

function ScoreBreakdown({
  score,
  confidence,
  signals = [],
  expanded = false,
  className = '',
}: ScoreBreakdownProps) {
  const ranked = sortSignals(signals);
  const visible = expanded ? ranked : ranked.slice(0, 3);
  const maxContribution = ranked.reduce((max, s) => Math.max(max, Math.abs(s.contribution)), 0);

  return (
    <div className={`space-y-2 ${className}`.trim()} aria-label={`Lead score ${score} out of 100`}>
      <div className="flex items-baseline justify-between">
        <div className="flex items-baseline">
          <span className="text-lg font-semibold tabular-nums text-fg">{score}</span>
          <span className="text-xs text-fg-muted">/ 100</span>
        </div>
        {confidence != null && confidence > 0 ? (
          <span className="text-xs text-fg-muted tabular-nums">{Math.round(confidence * 100)}% confidence</span>
        ) : null}
      </div>

      {visible.length > 0 ? (
        <ul className="space-y-2.5" aria-label="Score contributing signals">
          {visible.map((signal) => (
            <SignalRow key={signal.key} signal={signal} maxContribution={maxContribution} />
          ))}
        </ul>
      ) : (
        <p className="text-xs text-fg-muted">No structured signals yet — score uses profile completeness.</p>
      )}

      {!expanded && ranked.length > 3 ? (
        <p className="text-xs text-fg-muted">+{ranked.length - 3} more signal{ranked.length - 3 === 1 ? '' : 's'}</p>
      ) : null}
    </div>
  );
}

export default ScoreBreakdown;
